import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'
import { assistantEffects } from '@/lib/assistant-effects.generated'

import { reportingApi, type SaveReportBody } from '../api/reporting-api'
import { reportingKeys } from '../api/query-keys'

export const reportingEffectMeta = { assistantEffects: [assistantEffects.reportingChanged] } as const

export const notForbidden = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && (error.status === 403 || error.status === 404)) && failureCount < 2

export function useSaveReport(entityId: string | null, reportId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveReportBody) => {
      if (entityId === null) throw new Error('Select an entity before saving a report.')
      return reportId === null
        ? reportingApi.create(entityId, body)
        : reportingApi.update(entityId, reportId, body)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: reportingKeys.root }),
  })
}

export function useDuplicateReport(entityId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (reportId: string) => {
      if (entityId === null) throw new Error('Select an entity before duplicating a report.')
      return reportingApi.duplicate(entityId, reportId)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: reportingKeys.root }),
  })
}

export function useDeleteReport(entityId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (reportId: string) => {
      if (entityId === null) throw new Error('Select an entity before deleting a report.')
      return reportingApi.remove(entityId, reportId)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: reportingKeys.root }),
  })
}

export function useForceDeleteReport(entityId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (reportId: string) => {
      if (entityId === null) throw new Error('Select an entity before deleting a report.')
      return reportingApi.remove(entityId, reportId, true)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: reportingKeys.root }),
  })
}
